/* eslint-disable max-lines */
import {
  useGetAlarmNotificationPreference,
  useLocationAlarms,
  useViewLocationAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  DataVersionToggle,
  InvestigationViewSkeleton,
  ListItemSkeleton,
  ListView,
} from '@hakimo-ui/hakimo/feature-shared';
import {
  LocationAlarm,
  LocationAlarmsDTO,
  TimePeriod,
} from '@hakimo-ui/hakimo/types';
import { FilterPanel } from '@hakimo-ui/hakimo/ui-filters';
import { PageLayout } from '@hakimo-ui/hakimo/ui-layout';
import {
  audioNotifyAtom,
  isUnifiedDataVersion,
  systemNotifyAtom,
  toast,
  trackFilters,
  useOnDataVersionChange,
  withAuthz,
} from '@hakimo-ui/hakimo/util';
import { ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import FiltersHeader from '../shared/components/filters-header/FiltersHeader';
import { LOCATION_ALARM_QUERY_INTERVAL } from '../shared/constants';
import { useWithTimeFilterValue } from '../shared/hooks/useWithTimeFilterValue';
import { notifyNewAlarm } from '../shared/util/common';
import { getSearchParams } from '../shared/util/request-util';
import { defaultFilterValues, getFilterConfigs } from './constants';
import ListItem from './list-item/ListItem';
import { LocationAlarmDetailsData } from './location-alarm-details/LocationAlarmDetailsRoot';
import {
  alarmFiltersAtom,
  alarmReturnPostResolveAtom,
  pageAtom,
  pageSizeAtom,
  timePeriodAtom,
} from './state';
import { LocationAlarmFilterValues } from './types';

dayjs.extend(utc);

export function LocationAlarmsList() {
  const [page, setPage] = useAtom(pageAtom);
  const pageSize = useAtomValue(pageSizeAtom);
  const [period, setPeriod] = useAtom(timePeriodAtom);
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedPartitionKey, setSelectedPartitionKey] = useState<string>();
  const [alarmFilters, setAlarmFilters] = useAtom(alarmFiltersAtom);
  const alarmListFilters = useWithTimeFilterValue(alarmFilters, period);
  const prevData = useRef<LocationAlarmsDTO>();
  const isFirstDataAfterFlip = useRef(false);
  const audioNotify = useAtomValue(audioNotifyAtom);
  const alarmReturnPostResolve = useAtomValue(alarmReturnPostResolveAtom);
  const listref = useRef<HTMLDivElement>(null);
  const [locAlarmData, setLocAlarmData] = useState<LocationAlarm[]>([]);
  const systemNotifyPreference = useAtomValue(systemNotifyAtom);
  // Clearing the rows matters as much as clearing the selection: the query
  // keeps previous data, so the other dataset would stay on screen until the
  // new fetch lands and the skeleton branch below would never fire. `total`
  // goes with them so pagination cannot describe a list that is not there.
  const apiDataVersion = useOnDataVersionChange(() => {
    setSelectedId(undefined);
    setSelectedPartitionKey(undefined);
    setLocAlarmData([]);
    setTotal(0);
    prevData.current = undefined;
    isFirstDataAfterFlip.current = true;
  });
  const isV3 = isUnifiedDataVersion(apiDataVersion);
  // `page` is a jotai atom, so it cannot be reset during render like the
  // selection above -- writing an external store mid-render re-renders every
  // subscriber.
  useEffect(() => {
    setPage(1);
  }, [apiDataVersion, setPage]);
  // The "Resolution" (isEscalated) filter is v3-only -- v2's deployed
  // backend never reads that param, so v2 users never see it, and a value
  // set while previewing v3 never leaks into a v2 request either.
  const locationAlarmFilterConfigs = getFilterConfigs(isV3);
  const effectiveAlarmListFilters = isV3
    ? alarmListFilters
    : { ...alarmListFilters, escalated: undefined };
  const viewMutation = useViewLocationAlarm(undefined, apiDataVersion);
  const changeSelected = (alarm?: LocationAlarm) => {
    if (alarm) {
      setSelectedId(alarm.id);
      setSelectedPartitionKey(dayjs.utc(alarm.alarmTime).format('YYYYMM'));
    } else {
      setSelectedId(undefined);
      setSelectedPartitionKey(undefined);
    }
    if (alarm && alarm.id !== selectedId) {
      viewMutation.mutate({ alarmId: alarm.id });
    }
  };
  const { data: alarmNotificationSoundData } =
    useGetAlarmNotificationPreference();
  useEffect(() => {
    if (alarmFilters.camera === undefined) {
      setAlarmFilters(defaultFilterValues);
    }
  });

  const onApplyFilters = (values: LocationAlarmFilterValues) => {
    setAlarmFilters(values);
    trackFilters(values);
    setPage(1);
    setOpenFilterPanel(false);
  };

  const onData = (data: LocationAlarmsDTO) => {
    setTotal(data.total);
    setLocAlarmData(data.items);
    // After a flip there is no previous page to diff against, so the first
    // response is diffed against itself to avoid a false new-alarm alert.
    const baseline = isFirstDataAfterFlip.current
      ? data.items
      : prevData.current?.items;
    isFirstDataAfterFlip.current = false;
    if (data.items.length > 0) {
      notifyNewAlarm(
        baseline,
        data.items,
        page,
        'locationAlarm',
        audioNotify,
        systemNotifyPreference,
        alarmNotificationSoundData
      );
      prevData.current = data;
      if (!selectedId) {
        setFirstAlarmSelected(data.items);
        return;
      } else {
        const isSelectedAlarmExist = data.items.some(
          (item) => item.id === selectedId
        );
        if (!isSelectedAlarmExist) {
          toast(
            'Selected alarm may have moved to next page or is not available any more. Selecting the latest alarm'
          );
          setFirstAlarmSelected(data.items);
        }
      }
    }
  };
  const { error, isFetching } = useLocationAlarms(
    getSearchParams(page, pageSize, effectiveAlarmListFilters, undefined, true),
    LOCATION_ALARM_QUERY_INTERVAL,
    onData,
    false,
    true,
    apiDataVersion
  );

  useEffect(() => {
    if (error) {
      toast('Error getting location alarms', { type: 'error' });
    }
  }, [error]);

  const onAlarmResolve = useCallback(() => {
    if (alarmReturnPostResolve) {
      setSelectedId(undefined);
      setSelectedPartitionKey(undefined);
    }
  }, [alarmReturnPostResolve]);

  const handleChangePage = (pageNum: number) => {
    setPage(pageNum);
    changeSelected(undefined);
  };

  const handlePeriodChange = (
    newPeriod: TimePeriod,
    customRange?: { start: number; end: number }
  ) => {
    setPeriod(newPeriod);
    if (newPeriod === TimePeriod.CUSTOM_RANGE && customRange) {
      setAlarmFilters((prev) => ({
        ...prev,
        time: {
          period: TimePeriod.CUSTOM_RANGE,
          customRangeStart: customRange.start,
          customRangeEnd: customRange.end,
        },
      }));
    } else {
      setAlarmFilters((prev) => ({
        ...prev,
        time: {
          period: newPeriod,
          customRangeStart: 0,
          customRangeEnd: 0,
        },
      }));
    }
    setPage(1);
  };

  const setFirstAlarmSelected = (alarms: LocationAlarm[]) => {
    alarms.length > 0 && changeSelected(alarms[0]);
    listref && listref.current?.scrollTo(0, 0);
  };

  return (
    <PageLayout
      variant="split"
      title="Location Alarms"
      icon={ArrowsPointingInIcon}
    >
      <PageLayout.List>
        <FiltersHeader
          period={period}
          onChangePeriod={handlePeriodChange}
          onClickOpenFilterPanel={() => setOpenFilterPanel(true)}
          filterValues={alarmFilters}
          isLoading={isFetching}
          endSlot={<DataVersionToggle />}
        />
        <div
          ref={listref}
          className="overflow-auto scroll-smooth px-1 pb-4 pt-2"
        >
          {isFetching && locAlarmData.length === 0 ? (
            <div className="space-y-1">
              {Array.from({ length: pageSize }).map((_, i) => (
                <ListItemSkeleton key={i} />
              ))}
            </div>
          ) : (
            <ListView
              items={locAlarmData}
              total={total}
              page={page}
              pageSize={pageSize}
              onChangePage={handleChangePage}
              renderListItem={(alarm) => (
                <ListItem
                  alarm={alarm}
                  selected={alarm.id === selectedId}
                  onClick={() => changeSelected(alarm)}
                />
              )}
            />
          )}
        </div>
      </PageLayout.List>
      <PageLayout.Details>
        <div className="h-full">
          {selectedId && selectedPartitionKey ? (
            <LocationAlarmDetailsData
              locationAlarmId={selectedId}
              partitionKey={selectedPartitionKey}
              onAlarmResolve={onAlarmResolve}
              dataVersion={apiDataVersion}
            />
          ) : (
            isFetching &&
            locAlarmData.length === 0 && <InvestigationViewSkeleton />
          )}
        </div>
      </PageLayout.Details>
      <FilterPanel
        title="Location Alarm Filters"
        open={openFilterPanel}
        filterConfigs={locationAlarmFilterConfigs}
        initialFilterValues={alarmFilters}
        trackingEventNameApply="apply_location_alarms_filters"
        trackingEventNameReset="reset_location_alarms_filters"
        onClose={() => setOpenFilterPanel(false)}
        onApply={onApplyFilters}
        // Location filter normally excludes vision-source locations
        // (isAlarmGroups=false -> sourceSystem=vision,negated). On v3 those
        // alarms are unified into location_alarms too, so drop the exclusion.
        isAlarmGroups={isV3 ? undefined : false}
      />
    </PageLayout>
  );
}
export default withAuthz(LocationAlarmsList, ['location_alarm:view']);
